import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : low level react components for job system
// =============================================================================
// ---Third-party imports
import React from 'react';
import { differenceInMilliseconds } from 'date-fns';
import PropTypes from 'prop-types';
import { Table, Input, Item, Menu, Checkbox, Dropdown, Button, Segment, Modal, Header, Icon, Label, Divider } from 'semantic-ui-react';
import clsx from 'clsx';
import KeyHandler, { KEYPRESS, KEYDOWN } from 'react-key-handler';
// ---Framework imports
import { get, size, values, sorted, items, keys, sum, flatten, has, lstrip, mapItems, filterItems, dict, isArray, isFunction, isNil, strip, groupBy, callIfFunc } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import { useSelector, useState, useEffect, usePartContext, useMemo, useActions, useRouter, useApi, useRef } from 'uix/hooks';
import { funcPart } from 'uix/parts';
import log from 'uix/logging';
import connect from 'uix/connect';
import { AutoPortal, SelectableTable, Link, DayPicker, XMenu, XPopup, DebugFeature, Dialog } from 'uix/components';
import { filterNumbers, filterOptions } from 'uix/components/table/cols';
import { prettyBytes, formatDateTime, formatDistance, ucFirst } from 'uix/utils';
import { JobTemplates } from 'uix/registry';
import { VIcon } from 'uix/graphics';
import { Markdown, JSONTree } from 'uix/chunks';
// ---Local imports
import { loadJobSpec, startJob, deleteJobs } from './actions';
import styles from './styles.scss';
import { useJobs, useJobInfo, useLastJob, useJobEvents, useJobInfos, useActiveJobInfo, useJobFilterApi, ACTIVE_STATES } from './hooks';
import { JobReport, EventTypeIndicators } from './reports';
import { state_icons } from './utils';
// ---Exports
export const DEFAULT_DAYS = 3;
// ZipCapsuleStatusJob -> ZipCapsuleStatus
export const cleanName = (jobname)=>jobname.endsWith('Job') ? jobname.slice(0, -3) : jobname;
let DomainDropDown = ({ onSelect, ...props })=>{
    let subdomains = useSelector(Sel.subdomains);
    let dnames = subdomains.map((e)=>e.name);
    let request = useSelector(Sel.request);
    // use all domain names if root domain
    let domains = useSelector(Sel.domains);
    if (request.dname == domains.rootDomain) {
        dnames = domains.names;
    }
    props = {
        ...props,
        value: props.value || request.dname
    };
    let opts = dnames.map((v)=>{
        return {
            key: v,
            value: v,
            text: v,
            onClick: ()=>onSelect(v)
        };
    });
    if (!dnames.includes(props.value)) {
        opts = [
            {
                key: props.value,
                value: props.value,
                text: `${props.value} [unknown]`,
                onClick: ()=>null
            },
            ...opts
        ];
    }
    return /*#__PURE__*/ _jsx(Dropdown, {
        options: opts,
        ...props
    });
};
let getEditor = (name, type, defaultValue, options, edited, onEdit, onSubmit)=>{
    let res = null;
    let setValue = (value)=>{
        // undefined values will not be send over api
        if (value === '') {
            value = undefined;
        } else if ([
            'boolean'
        ].includes(type) && value == defaultValue) {
            value = undefined;
        }
        onEdit(name, value);
    };
    if (type == 'boolean') {
        // checkbox needs always a value
        let checked = get(edited, name, defaultValue);
        res = /*#__PURE__*/ _jsx(Checkbox, {
            toggle: true,
            checked: checked === true || String(checked).toLowerCase() === 'true',
            onChange: (event, data)=>setValue(data.checked === true)
        });
    } else if (type == 'option') {
        let value = edited[name] || null;
        if (name === 'job.domainName' || name === 'event.domain') {
            res = /*#__PURE__*/ _jsx(DomainDropDown, {
                onSelect: (v)=>setValue(v),
                value: value,
                placeholder: defaultValue,
                fluid: true,
                selection: true
            });
        } else {
            // && name !== 'job.domainName'
            let opts = options.map((v)=>{
                return {
                    key: v,
                    value: v,
                    text: v,
                    onClick: ()=>setValue(v)
                };
            });
            // XXX: clearable option would be nice, but doesnt work out
            res = /*#__PURE__*/ _jsx(Dropdown, {
                value: value,
                placeholder: defaultValue,
                fluid: true,
                selection: true,
                options: opts
            });
        }
    } else {
        let value = edited[name] || '';
        let props = {};
        let autocomplete = 'on';
        let input_type = 'text';
        if (name.includes('password')) {
            input_type = 'password';
            autocomplete = 'off';
        }
        res = /*#__PURE__*/ _jsx(Input, {
            value: value,
            placeholder: defaultValue,
            onChange: (e, data)=>setValue(data.value),
            type: input_type,
            onKeyDown: (e)=>onSubmit && e.key === 'Enter' ? onSubmit() : null,
            autoComplete: autocomplete,
            fluid: true
        });
    }
    return res;
};
export const compareEditorValueToDefault = (value, defaultValue)=>{
    if (isNil(defaultValue)) {
        defaultValue = '';
    }
    if (isNil(value)) {
        value = defaultValue;
    }
    if (isNil(value)) {
        value = '';
    }
    if (isArray(defaultValue)) {
        defaultValue = defaultValue.join(',');
    }
    return String(value) != String(defaultValue);
};
const EmailTemplateDiagnostics = ({ jobName })=>{
    let dlg = null;
    let [open, setOpen] = useState(false);
    let { data } = useApi('job/getEmailTemplateDiagnostics', {
        name: jobName
    }, {
        condition: open
    });
    if (open) {
        dlg = /*#__PURE__*/ _jsx(Dialog, {
            open: true,
            onClose: ()=>setOpen(false),
            children: /*#__PURE__*/ _jsx(JSONTree, {
                hideRoot: true,
                data: data,
                shouldExpandNode: ()=>true
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                compact: true,
                icon: "mail",
                basic: true,
                size: "mini",
                floated: "right",
                onClick: ()=>setOpen(!open)
            }),
            dlg
        ]
    });
};
export const SpecSections = ({ jobName, sections, edited, onEdit, resetEdited, onSubmit, showParamDocs = false, showDefaultChanges = false })=>{
    let renderName = (section, e, isModified)=>{
        let dottedName = `${section}.${e.name}`;
        let label = e.name;
        if (dottedName == 'job.domainName') {
            label = 'domain';
        }
        if (showDefaultChanges && isModified) {
            label = /*#__PURE__*/ _jsx("span", {
                style: {
                    color: 'blue'
                },
                children: label
            });
        }
        if (dottedName == 'job.notify') {
            label = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(DebugFeature, {
                        children: /*#__PURE__*/ _jsx(EmailTemplateDiagnostics, {
                            jobName: jobName
                        })
                    }),
                    label
                ]
            });
        }
        return label;
    };
    let secs = sections.map(([section, scalars])=>{
        let rows = scalars.map((e)=>{
            let n = section ? `${section}.${e.name}` : e.name;
            // XXX: line breaks?
            let comment = e.comment.split('#').map((l)=>strip(strip(l), '#')).join('\n');
            comment = `${comment} [default: ${e.default}]`;
            let editorRow = /*#__PURE__*/ _jsxs(Table.Row, {
                className: "scalar",
                title: comment,
                children: [
                    /*#__PURE__*/ _jsx(Table.Cell, {
                        children: renderName(section, e, compareEditorValueToDefault(edited[n], e.default))
                    }),
                    /*#__PURE__*/ _jsx(Table.Cell, {
                        children: getEditor(n, e.type, e.default, e.options, edited, onEdit, onSubmit)
                    })
                ]
            }, n);
            if (showParamDocs && comment) {
                return /*#__PURE__*/ _jsxs(React.Fragment, {
                    children: [
                        editorRow,
                        /*#__PURE__*/ _jsx(Table.Row, {
                            children: /*#__PURE__*/ _jsx(Table.Cell, {
                                colspan: "2",
                                children: /*#__PURE__*/ _jsx("i", {
                                    children: comment
                                })
                            })
                        })
                    ]
                }, n);
            }
            return editorRow;
        });
        if (!section) {
            var head = /*#__PURE__*/ _jsx(Table.Row, {
                className: "section rootSection",
                children: /*#__PURE__*/ _jsxs(Table.Cell, {
                    colSpan: "2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            icon: "eraser",
                            basic: true,
                            onClick: ()=>resetEdited(),
                            floated: "right",
                            size: "tiny",
                            title: "Reset",
                            disabled: !size(edited)
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: "Parameter"
                        })
                    ]
                })
            }, "jobName");
        } else {
            var head = /*#__PURE__*/ _jsx(Table.Row, {
                className: "section",
                children: /*#__PURE__*/ _jsx(Table.Cell, {
                    colSpan: "2",
                    children: ucFirst(section)
                })
            }, section);
        }
        rows = [
            head,
            ...rows
        ];
        return rows;
    });
    secs = flatten(secs);
    return /*#__PURE__*/ _jsx(Table, {
        definition: true,
        className: "jobParams",
        children: /*#__PURE__*/ _jsx(Table.Body, {
            children: secs
        })
    });
};
// get active job given by name
const getActive = (name, active)=>active.filter((job)=>job.name == name);
const goJobId = (go, classname, tabname, ojid)=>{
    if (tabname == 'job') {
        // assume we already are on a job-id-processing tab
        go({
            id: `j${ojid}`
        });
    } else {
        go({
            classname: 'job',
            tabname: 'id',
            id: `j${ojid}`
        });
    }
};
const useRequestedJobId = ()=>{
    let request = useSelector(Sel.request);
    return lstrip(request.id, 'j');
};
const JobItem = ({ job, named = false, level = 0 })=>{
    let { go, request } = useRouter();
    let req_ojid = useRequestedJobId();
    let icon = {
        name: 'cog'
    };
    if (job.state == 'RUNNING') {
        icon = {
            ...icon,
            loading: true,
            color: 'green'
        };
    } else if (job.state == 'NEW' || job.state.includes('STARTING')) {
        icon = {
            ...icon,
            loading: true
        };
    }
    let content = named ? cleanName(job.name) : `Job ${job.ojid}`;
    if (level > 0) {
        content = /*#__PURE__*/ _jsxs("span", {
            style: {
                paddingLeft: `${5 * (level - 1)}px`,
                fontSize: level > 0 ? '0.9rem' : undefined
            },
            children: [
                /*#__PURE__*/ _jsx("b", {
                    children: "⤷"
                }),
                " ",
                content
            ]
        });
    }
    return /*#__PURE__*/ _jsx(Menu.Item, {
        active: req_ojid == job.ojid,
        content: content,
        icon: icon,
        title: `${job.ojid}: ${job.state}`,
        onClick: ()=>goJobId(go, request.classname, request.tabname, job.ojid)
    });
};
const ActiveJobItems = ({ jobname, selfAndAxisJobs })=>{
    let ojid2info = useSelector((root)=>root.data.job.info);
    let ojid2axisjobs = dict(selfAndAxisJobs.map((j)=>[
            j.ojid,
            j
        ]));
    let jobinfo = (j)=>({
            ...j,
            ...ojid2info[j.ojid] || {}
        });
    let active = useSelector((root)=>root.data.job.active);
    let activeIds = new Set(active.map((j)=>j.ojid));
    let activeSameName = getActive(jobname, active);
    let activeSameNameIds = new Set(activeSameName.map((j)=>j.ojid));
    // merge active jobs and axis jobs
    let interesting = [
        ...activeSameName,
        ...selfAndAxisJobs.filter((j)=>!activeSameNameIds.has(j.ojid))
    ];
    let similar = interesting // getActive(jobname, interesting)
    ;
    let similarIds = new Set(similar.map((j)=>j.ojid));
    let buildActiveChildren = (job, level = 1)=>similar.filter((j)=>jobinfo(j).ojparent == job.ojid).map((j)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(JobItem, {
                        named: true,
                        level: level,
                        job: jobinfo(j)
                    }),
                    buildActiveChildren(j, level + 1)
                ]
            }, `job_${job.ojid}_${j.ojid}`));
    let notNested = (j)=>!similarIds.has(jobinfo(j).ojparent);
    let roots = similar.filter(notNested);
    return roots.map((j, idx)=>/*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                /*#__PURE__*/ _jsx(JobItem, {
                    job: jobinfo(j)
                }),
                buildActiveChildren(j)
            ]
        }, `active_${j.ojid}`));
};
const LastJobItem = ({ jobname })=>{
    let seen = useSelector((root)=>root.data.job.seen) || [];
    let ojid2info = useSelector((root)=>root.data.job.info);
    let jobinfo = (j)=>ojid2info[j.ojid] || j;
    seen = seen.filter((job)=>job.name == jobname && job.state !== 'NEW').slice(-1)[0];
    if (seen) {
        return /*#__PURE__*/ _jsx(JobItem, {
            job: jobinfo(seen)
        });
    }
    return null;
};
const JobLabelBase = ({ ojid, job, placeholder })=>{
    let content = `${job?.name || ''}`;
    if (!job) {
        if (placeholder) {
            content = placeholder;
        }
    }
    let isActive = ACTIVE_STATES.has(job?.state);
    return /*#__PURE__*/ _jsx(Link, {
        classname: "job",
        tabname: "id",
        id: `j${ojid}`,
        children: /*#__PURE__*/ _jsxs(Label, {
            title: `Job ${ojid}`,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "cog",
                    loading: isActive,
                    color: isActive ? 'green' : undefined
                }),
                content,
                /*#__PURE__*/ _jsx(Label.Detail, {
                    children: ojid
                })
            ]
        })
    });
};
export const JobLabel = ({ ojid, placeholder })=>{
    let job = useActiveJobInfo(ojid);
    return /*#__PURE__*/ _jsx(JobLabelBase, {
        ojid: ojid,
        placeholder: placeholder,
        job: job
    });
};
const JobButton = ({ name, label, title, schedulerFocus = false, showOptions = false, hideSubmenu = false, params = {}, onOptions, disabled = false, basic = false, attached = false, onJobReady, trackActivity = false })=>{
    let { specLoaded, jobSpec, startJob, active } = useJobs();
    if (!trackActivity) {
        active = [];
    }
    let isUnknownJob = !jobSpec[name];
    let [isActive, setIsActive] = useState(false);
    let activeJobs = getActive(name, active);
    useEffect(()=>{
        let value = activeJobs.length > 0;
        if (isActive && !value && onJobReady) {
            onJobReady();
        }
        setIsActive(value);
    }, [
        activeJobs.length
    ]);
    let props = {};
    if (!isUnknownJob) {
        props.icon = /*#__PURE__*/ _jsxs(Icon.Group, {
            children: [
                /*#__PURE__*/ _jsx(VIcon, {
                    name: "cog"
                }),
                /*#__PURE__*/ _jsx(VIcon, {
                    corner: true,
                    name: showOptions ? 'configure' : 'play',
                    color: showOptions ? 'black' : 'green'
                })
            ]
        });
        props.title = title || `Starte Job: ${name}`;
        if (isActive) {
            props.icon = {
                name: 'cog',
                loading: true,
                color: 'green'
            };
        }
        if (showOptions) {
            props.onClick = ()=>onOptions({});
        } else {
            props.onClick = ()=>startJob(name, stringifyJobParams(params));
        }
    }
    if (isUnknownJob) {
        return /*#__PURE__*/ _jsx(BasicButton, {
            content: name,
            loading: !specLoaded,
            icon: {
                name: 'question',
                color: 'red'
            }
        });
    } else {
        let buttons = null;
        if (schedulerFocus) {
            buttons = /*#__PURE__*/ _jsx(SchedulerButton, {
                name: name,
                isActive: isActive,
                onOptions: onOptions
            });
        } else {
            buttons = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(BasicButton, {
                        content: label || cleanName(name),
                        loading: !specLoaded,
                        ...props,
                        disabled: disabled
                    }),
                    !hideSubmenu && /*#__PURE__*/ _jsx(ButtonSubmenu, {
                        disabled: disabled,
                        onOptions: onOptions
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsx(Button.Group, {
            attached: attached,
            children: buttons
        });
    }
};
const ButtonSubmenu = ({ disabled, onOptions })=>{
    const dd_items = [
        {
            key: 'options',
            icon: 'edit',
            text: 'Options',
            onClick: ()=>onOptions({
                    showJobOptsDefault: true
                })
        },
        {
            key: 'history',
            icon: 'history',
            text: 'History',
            onClick: ()=>onOptions({
                    showHistDefault: true
                })
        }
    ];
    return /*#__PURE__*/ _jsx(Dropdown, {
        basic: true,
        className: "icon",
        options: dd_items,
        trigger: /*#__PURE__*/ _jsx(_Fragment, {}),
        button: true
    });
};
const BasicButton = (props)=>/*#__PURE__*/ _jsx(Button, {
        basic: true,
        compact: true,
        size: "small",
        ...props
    });
let recoverCaseHtml5 = (params, spec)=>{
    /* html5 attribute names are always lowercase,
  we restore the casing by checking the names of the job config_ini (spec)
  param.mystuff => param.myStuff
  */ let lcmap = dict(flatten(mapItems(spec, (name, opts)=>opts.map((j)=>`${name}.${j.name}`))).map((n)=>lstrip(n, '.')).map((n)=>[
            n.toLowerCase(),
            n
        ]));
    return dict(mapItems(params, (n, v)=>[
            get(lcmap, n.toLowerCase(), n),
            v
        ]));
};
export const JobWikiButton = ({ name, confirm = false, params = {} })=>{
    let { specLoaded, jobSpec } = useJobs();
    let [fullOptions, setFullOptions] = useState(false);
    if (specLoaded && size(params) > 0 && jobSpec[name]) {
        params = recoverCaseHtml5(params, jobSpec[name].spec);
    }
    let showOptions = !!confirm;
    if (!specLoaded || !fullOptions) {
        return /*#__PURE__*/ _jsx(JobButton, {
            name: name,
            params: params,
            showOptions: showOptions,
            onOptions: (opts)=>setFullOptions(opts),
            trackActivity: true
        });
    } else {
        return /*#__PURE__*/ _jsxs(Segment, {
            children: [
                /*#__PURE__*/ _jsx(KeyHandler, {
                    keyEventName: KEYDOWN,
                    code: 'Escape',
                    onKeyHandle: ()=>setFullOptions(false)
                }),
                /*#__PURE__*/ _jsx(Item.Group, {
                    children: /*#__PURE__*/ _jsx(Job, {
                        name: name,
                        showName: true,
                        params: params,
                        spec: jobSpec[name].spec,
                        ...fullOptions,
                        children: /*#__PURE__*/ _jsx(Menu.Item, {
                            content: "Close",
                            icon: {
                                name: 'close',
                                color: 'red'
                            },
                            onClick: ()=>setFullOptions(false)
                        })
                    })
                })
            ]
        });
    }
};
export const JobMenuButton = ({ name, label, title, schedulerFocus = false, confirm = false, hideSubmenu = false, subheader, disabled = false, inactive = false, params = {}, onJobReady })=>{
    let { specLoaded, jobSpec, startJob } = useJobs();
    let [fullOptions, setFullOptions] = useState(false);
    let showOptions = false // JobButton will start the job directly
    ;
    let renderCustomDialog = null;
    if (confirm) {
        // show option confirmation
        showOptions = true;
        if (confirm !== true) {
            if (isFunction(confirm)) {
                renderCustomDialog = confirm;
            } else {
                log.warn('unknown confirm format');
            }
        } else {
            renderCustomDialog = JobTemplates[name]?.dialog;
        }
    }
    let dialog = null;
    let open = !!fullOptions;
    let needJobDetails = fullOptions?.showHistDefault || fullOptions?.showJobOptsDefault;
    if (specLoaded) {
        let spec = jobSpec[name]?.spec;
        if (!spec) {
            log.warn('unknown job', name);
        } else if (!renderCustomDialog || needJobDetails) {
            let h = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    name,
                    fullOptions.message && /*#__PURE__*/ _jsx(Header.Subheader, {
                        children: fullOptions.message
                    })
                ]
            });
            dialog = /*#__PURE__*/ _jsx(Modal, {
                open: open,
                size: "large",
                onClose: ()=>setFullOptions(false),
                closeIcon: true,
                className: "L131Split",
                children: /*#__PURE__*/ _jsxs(Modal.Content, {
                    children: [
                        /*#__PURE__*/ _jsx(Header, {
                            icon: "cog",
                            content: h,
                            subheader: subheader
                        }),
                        /*#__PURE__*/ _jsx(Item.Group, {
                            children: open && /*#__PURE__*/ _jsx(Job, {
                                name: name,
                                spec: spec,
                                params: params,
                                ...fullOptions,
                                children: /*#__PURE__*/ _jsx(Menu.Item, {
                                    content: "Close",
                                    icon: {
                                        name: 'close',
                                        color: 'red'
                                    },
                                    onClick: ()=>setFullOptions(false)
                                })
                            })
                        })
                    ]
                })
            });
        } else {
            // renderCustomDialog can be a locally scoped func
            // so do not use React.createElement here (or all child elements will be remounted on rerender)
            dialog = renderCustomDialog({
                open,
                spec,
                params,
                closeDialog: ()=>setFullOptions(false),
                startJob
            });
        }
    }
    return /*#__PURE__*/ _jsxs(XMenu.Item, {
        fitted: true,
        children: [
            /*#__PURE__*/ _jsx(JobButton, {
                name: name,
                showOptions: showOptions,
                hideSubmenu: hideSubmenu,
                label: label,
                title: title,
                schedulerFocus: schedulerFocus,
                basic: true,
                attached: true,
                params: params,
                trackActivity: !inactive,
                disabled: disabled,
                onOptions: (opts)=>setFullOptions(opts),
                onJobReady: onJobReady
            }),
            dialog
        ]
    });
};
const SchedulerButton = ({ name, isActive, onOptions })=>{
    /* JobButton variant that visualizes first(!) matching scheduled job
   */ let { data, error, reload } = useApi('filter/getSchedulerInfo', {}, {
        autoreset: false
    });
    let callApi = useActions('callApi');
    let indexSchedulerEvents = (entries)=>{
        let byEventName = {};
        let byJobName = {};
        let traverse = (evts, parentName)=>{
            for (let e of evts){
                e = {
                    ...e,
                    parentName
                };
                byEventName[e.eventName] = e;
                if (!byJobName[e.eventJob]) byJobName[e.eventJob] = [];
                byJobName[e.eventJob].push(e);
                traverse(e.eventChildren, e.eventName);
            }
        };
        traverse(entries, null);
        return {
            byEventName,
            byJobName,
            entries
        };
    };
    let { byEventName, byJobName, entries } = useMemo(()=>indexSchedulerEvents(data?.scheduler.events || []), [
        data
    ]);
    let button = /*#__PURE__*/ _jsx(BasicButton, {
        icon: "calendar outline",
        content: name,
        title: "Not scheduled"
    });
    let scheduler_enabled = data?.scheduler.enabled === true;
    if (size(byJobName[name]) > 0) {
        let entry = byJobName[name][0];
        let { eventActive, eventName } = entry;
        let icon = /*#__PURE__*/ _jsxs(Icon.Group, {
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "calendar outline"
                }),
                eventActive ? /*#__PURE__*/ _jsx(Icon, {
                    corner: true,
                    name: "play circle",
                    color: "green"
                }) : /*#__PURE__*/ _jsx(Icon, {
                    corner: true,
                    name: "stop circle",
                    color: "red"
                })
            ]
        });
        let _onOptions = (opts)=>{
            opts = {
                ...opts,
                params: entry.params,
                message: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        'Parameter: ',
                        /*#__PURE__*/ _jsx(Icon, {
                            name: "calendar outline"
                        }),
                        eventName
                    ]
                })
            };
            onOptions(opts);
        };
        let setEventActive = (active)=>{
            let apiName = active ? 'enableEvent' : 'disableEvent';
            callApi(apiName, {
                eventName
            }, reload);
        };
        button = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                isActive && /*#__PURE__*/ _jsx(BasicButton, {
                    icon: {
                        name: 'cog',
                        loading: true,
                        color: 'green'
                    }
                }),
                /*#__PURE__*/ _jsx(BasicButton, {
                    title: eventActive ? `Next execution in ${entry.eventNext}` : 'Inactive',
                    icon: icon,
                    active: eventActive,
                    disabled: !scheduler_enabled,
                    content: eventName,
                    onClick: ()=>setEventActive(!eventActive)
                }),
                /*#__PURE__*/ _jsx(ButtonSubmenu, {
                    onOptions: _onOptions
                })
            ]
        });
    }
    return button;
};
export const stringifyJobParams = (params)=>dict(mapItems(params, (k, v)=>{
        if (isArray(v)) {
            return [
                k,
                v.join(',')
            ];
        } else {
            return [
                k,
                String(v)
            ];
        }
    }));
export const useEditing = (params, keepDeleted = false)=>{
    let [_edited, setEdited] = useState(null);
    let externalParams = stringifyJobParams(params);
    let edited = {
        ...externalParams,
        ..._edited || {}
    };
    let setEditValue = (name, value)=>{
        if (value !== undefined || keepDeleted) {
            setEdited({
                ...edited,
                [name]: value
            });
        } else {
            setEdited(dict(filterItems(edited, (k, v)=>k !== name)));
        }
    };
    let isModified = _edited !== null && filterItems(_edited, (k, v)=>externalParams[k] != v).length > 0;
    return [
        edited,
        setEditValue,
        (values)=>setEdited(values || {}),
        isModified
    ];
};
const JobHistory = connect('JobHistory', 'data.job.history', ({ name, history = {}, reduceApi })=>{
    let reducer = (dataState, result, error)=>{
        let { job } = dataState;
        let { history } = job;
        if (result && result.jobs) {
            return {
                job: {
                    ...job,
                    history: {
                        ...history,
                        [name]: result.jobs
                    }
                }
            };
        }
    };
    let days = 90;
    let max = 8;
    useEffect(()=>{
        if (!history[name]) {
            reduceApi('filter/getJobs', {
                names: name,
                max: 8,
                days
            }, reducer);
        }
    }, [
        history[name]
    ]);
    let jobs = useMemo(()=>history[name] || [], [
        history,
        name
    ]);
    // since we can get more results due to resolving of nested jobs
    // filter again down (assume they are sorted by ojid asc)
    if (size(jobs) > max) {
        jobs = jobs.slice(0, max + 1);
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.jobinfoPane,
        children: /*#__PURE__*/ _jsx(JobTable, {
            jobs: jobs,
            noDataText: `No Jobs for last ${days} days`,
            loading: !jobs,
            showPaginationTop: false,
            selectable: true
        })
    });
});
export const sectionGroupsFromSpec = (spec)=>{
    let sectionGroups = [];
    let isSchedulerEventSec = (section)=>section == 'event';
    // root section is first, job section is last and the others are sorted by number of items
    let sortedSecs = sorted(items(spec), ([section, scalars])=>!section ? 0 : section == 'job' ? 999 : isSchedulerEventSec(section) ? -1 : scalars.length);
    let scalarCount = sum(values(spec).map((e)=>e.length + 1));
    let fill = 0;
    let currentGroup = [];
    sectionGroups.push(currentGroup);
    let COLUMNS = 2;
    for (let [section, scalars] of sortedSecs){
        if (currentGroup.length > 0 && section !== '' && fill + scalars.length >= Math.floor(scalarCount / COLUMNS)) {
            currentGroup = [];
            sectionGroups.push(currentGroup);
            fill = 0;
        }
        fill += scalars.length;
        currentGroup.push([
            section,
            scalars
        ]);
        if (isSchedulerEventSec(section)) {
            currentGroup = [];
            sectionGroups.push(currentGroup);
            fill = 0;
        }
    }
    return sectionGroups;
};
const ForwardBackwardItem = ({ job, direction, icon })=>{
    let { go } = useRouter();
    let [goId, setGoId] = useState(null);
    let { data } = useApi(`job/${direction}`, {
        ojid: goId
    }, {
        condition: goId && goId > 0
    });
    useEffect(()=>{
        if (goId && data) {
            if (data.ojid) {
                go({
                    id: `j${data.ojid}`
                });
            } else {
                setGoId(-1); // no further movement
            }
        }
    }, [
        goId,
        data
    ]);
    useEffect(()=>{
        setGoId(null);
    }, [
        job?.ojid
    ]);
    return /*#__PURE__*/ _jsx(Menu.Item, {
        style: {
            flex: '1',
            textAlign: 'center'
        },
        // content={direction}
        className: clsx(styles.arrowItem, direction == 'next' ? styles.next : styles.prev),
        disabled: goId === -1,
        icon: icon,
        onClick: ()=>setGoId(job.ojid)
    });
};
function dedentDocstring(docstring) {
    // Split the string into lines
    let lines = docstring.split('\n');
    // Remove leading and trailing blank lines
    while(lines.length && lines[0].trim() === '')lines.shift();
    while(lines.length && lines[lines.length - 1].trim() === '')lines.pop();
    // Find the minimum indentation (ignoring empty lines)
    let minIndent = lines.filter((line)=>line.trim() !== '').reduce((min, line)=>{
        let match = line.match(/^(\s*)/);
        let indent = match ? match[0].length : 0;
        return Math.min(min, indent);
    }, Infinity);
    // Remove the minimum indentation
    let dedented = lines.map((line)=>line.slice(minIndent)).join('\n');
    return dedented;
}
export const JobDocs = ({ text })=>{
    // State for expansion toggle
    const [isExpanded, setIsExpanded] = useState(false);
    // State to determine if truncation controls are necessary
    const [needsTruncation, setNeedsTruncation] = useState(false);
    // Ref to measure content height
    const contentRef = useRef(null);
    const processedText = dedentDocstring(text);
    const maxHeightLimit = 200 // px
    ;
    // Check if content height exceeds the limit after render/text change
    useEffect(()=>{
        const checkHeight = ()=>{
            if (contentRef.current) {
                // Check if the scrollHeight (full content height) > the limit
                setNeedsTruncation(contentRef.current.scrollHeight > maxHeightLimit);
            }
        };
        // Use rAF to ensure layout calculation is done before checking height
        const animationFrameId = requestAnimationFrame(checkHeight);
        return ()=>cancelAnimationFrame(animationFrameId);
    }, [
        text,
        maxHeightLimit
    ]); // Re-run if text or limit changes
    const toggleExpansion = ()=>{
        setIsExpanded(!isExpanded);
    };
    // Determine if the component should currently be in a collapsed state
    const isCollapsed = needsTruncation && !isExpanded;
    // --- Inline Styles ---
    const containerStyle = {
        position: 'relative',
        maxHeight: isCollapsed ? `${maxHeightLimit}px` : 'none',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-out',
        // Prevent text hitting the fade abruptly when collapsed
        paddingBottom: isCollapsed ? '20px' : '0px'
    };
    const fadeStyle = {
        display: isCollapsed ? 'block' : 'none',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '50px',
        // Adjust gradient color if background isn't white
        background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
        pointerEvents: 'none'
    };
    const buttonStyle = {
        display: needsTruncation ? 'inline-block' : 'none',
        cursor: 'pointer',
        color: '#007bff',
        background: 'none',
        border: 'none',
        padding: '5px 0 0 0',
        marginTop: '5px',
        fontSize: '0.9em'
    };
    return /*#__PURE__*/ _jsx(Segment, {
        className: "jobDocs",
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    ref: contentRef,
                    style: containerStyle,
                    children: [
                        /*#__PURE__*/ _jsx(Markdown, {
                            children: processedText
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            style: fadeStyle
                        })
                    ]
                }),
                needsTruncation && /*#__PURE__*/ _jsx("button", {
                    onClick: toggleExpansion,
                    style: buttonStyle,
                    children: isExpanded ? '[show less]' : '[show all]'
                })
            ]
        })
    });
};
export const JobParamsPane = ({ jobName, spec, edited = {}, setEditValue, resetEdited, onSubmit, showParamDocs, showDefaultChanges })=>{
    return /*#__PURE__*/ _jsx("div", {
        className: styles.jobinfoPane,
        children: sectionGroupsFromSpec(spec).map((group, i)=>/*#__PURE__*/ _jsx("div", {
                style: {
                    flex: '1',
                    paddingLeft: i > 0 ? '10px' : null
                },
                children: /*#__PURE__*/ _jsx(SpecSections, {
                    jobName: jobName,
                    sections: group,
                    edited: edited,
                    onEdit: setEditValue,
                    resetEdited: resetEdited,
                    onSubmit: onSubmit,
                    showParamDocs: showParamDocs,
                    showDefaultChanges: showDefaultChanges
                })
            }, i))
    });
};
export const Job = ({ name, ojid, params, spec, showMenu = true, showHistDefault = false, showBackToList = false, resolveDependencies = false, children, showName = false, doc, showDocs = false, showParamDocs = false, onViewModeChange })=>{
    //~ let ojid = useSelector(root => root.meta.request.id)
    let { go } = useRouter();
    // events, options, history
    let [mode, setViewMode] = useState(null);
    useEffect(()=>{
        callIfFunc(onViewModeChange, mode);
    }, [
        mode
    ]);
    let { startJob } = useJobs();
    let jobInfos = useJobInfos({
        ojid,
        resolveDependencies
    });
    let job = jobInfos.job;
    useEffect(()=>{
        if (ojid) setViewMode('events');
    }, [
        ojid
    ]);
    if (showHistDefault && mode === null) {
        mode = 'history';
    }
    if (!mode) {
        mode = ojid ? 'events' : 'options';
    }
    let [edited, setEditValue, resetEdited] = useEditing(params);
    // load job params for ojid into editors
    // also on change to options
    useEffect(()=>{
        if (ojid && job?.kwds) {
            resetEdited(job.kwds);
        }
    }, [
        job,
        mode === 'options'
    ]);
    // setup Options for starting a parametrized job
    let options = ()=>/*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs(Item.Content, {
                style: {
                    flex: 1
                },
                children: [
                    showDocs && doc && /*#__PURE__*/ _jsx(JobDocs, {
                        text: doc
                    }),
                    /*#__PURE__*/ _jsx(JobParamsPane, {
                        jobName: name,
                        spec: spec,
                        edited: edited,
                        setEditValue: setEditValue,
                        resetEdited: resetEdited,
                        onSubmit: ()=>startJob(name, edited),
                        showParamDocs: showParamDocs
                    })
                ]
            })
        });
    let startClick = ()=>{
        if (mode == 'options') {
            startJob(name, edited);
        }
        setViewMode('options');
    };
    let menu = showMenu ? /*#__PURE__*/ _jsxs(XMenu, {
        className: styles.verticalMenu,
        padded: true,
        vertical: true,
        children: [
            ojid && /*#__PURE__*/ _jsxs("span", {
                style: {
                    display: 'flex'
                },
                children: [
                    /*#__PURE__*/ _jsx(ForwardBackwardItem, {
                        job: job,
                        direction: "prev",
                        icon: "arrow left"
                    }),
                    /*#__PURE__*/ _jsx(ForwardBackwardItem, {
                        job: job,
                        direction: "next",
                        icon: "arrow right"
                    })
                ]
            }),
            showName && /*#__PURE__*/ _jsx(Menu.Item, {
                content: cleanName(name),
                className: "jobName",
                title: name
            }),
            showBackToList && /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "arrow up",
                onClick: ()=>go({
                        id: null
                    }),
                content: "Liste"
            }),
            ojid && /*#__PURE__*/ _jsx(Menu.Item, {
                content: "Details",
                icon: "info",
                active: mode == 'events',
                onClick: ()=>setViewMode('events')
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                content: "Start",
                icon: mode == 'options' ? 'play' : 'configure',
                active: mode == 'options',
                onClick: startClick
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                content: "Historie",
                icon: "history",
                active: mode == 'history',
                onClick: ()=>setViewMode('history')
            }),
            /*#__PURE__*/ _jsx(Divider, {}),
            /*#__PURE__*/ _jsx(ActiveJobItems, {
                jobname: name,
                selfAndAxisJobs: jobInfos?.jobs
            }),
            !ojid && /*#__PURE__*/ _jsx(LastJobItem, {
                jobname: name
            }),
            children
        ]
    }) : null;
    return /*#__PURE__*/ _jsx(Item, {
        className: "job",
        children: /*#__PURE__*/ _jsxs(Segment, {
            style: {
                display: 'flex',
                width: '100%',
                flex: '1'
            },
            raised: true,
            children: [
                menu,
                mode == 'options' && options(),
                mode == 'events' && ojid && /*#__PURE__*/ _jsx(JobReport, {
                    name: name,
                    ojid: ojid,
                    jobinfo: job
                }),
                mode == 'history' && /*#__PURE__*/ _jsx(JobHistory, {
                    name: name
                })
            ]
        })
    });
};
export const DeleteJobButton = ({ disabled = false, jobs, onDelete })=>{
    let { deleteJobs } = useJobs();
    return /*#__PURE__*/ _jsx(XMenu.Item, {
        icon: "trash",
        disabled: disabled,
        confirm: `${jobs.length} Job(s) löschen?`,
        onClick: ()=>deleteJobs(jobs, ()=>onDelete && onDelete()),
        title: "Delete job(s)"
    });
};
let JobTableMenu = ({ jobs, selected = [] })=>{
    let part = usePartContext();
    return /*#__PURE__*/ _jsx(AutoPortal, {
        target: "JobMenuPortal",
        children: /*#__PURE__*/ _jsxs(XMenu, {
            children: [
                /*#__PURE__*/ _jsx(DebugFeature, {
                    children: /*#__PURE__*/ _jsx(DeleteJobButton, {
                        disabled: !selected.length,
                        jobs: selected,
                        onDelete: ()=>part.reload()
                    })
                }),
                /*#__PURE__*/ _jsx(DayPicker, {
                    defaultValue: DEFAULT_DAYS
                })
            ]
        })
    });
};
export const Jobs = ({ names, days = 7, ...props })=>{
    /* JobTable with fetched jobs list
   */ let { jobs, loading } = useJobFilterApi({
        names,
        days
    });
    return /*#__PURE__*/ _jsx(JobTable, {
        jobs: jobs,
        loading: loading,
        ...props
    });
};
export const JobTable = ({ jobs = [], listOptions, selectable = false, filterable = false, compact = false, flat = false, showPaginationTop = true, pageSize = undefined, defaultPageSize = 100, linkTarget = 'job/id', noDataText, loading, flex })=>{
    let part = usePartContext();
    let { domains, i18n, apiurl, dname } = part;
    let request = useSelector(Sel.request);
    // selection aspect
    // selected[0] stores toggleAll with 3 states: 0, 1, 2=indeterminate
    let [selected, setSelected] = useState({});
    let [changedPageSize, setPageSize] = useState(0);
    let Type = {
        5: i18n.job_type_manual,
        10: i18n.job_type_automatic
    };
    // link to undefined/undefined (current class/tab) if not given
    let [classname, tabname] = linkTarget ? linkTarget.split('/') : [];
    let index = useMemo(()=>{
        let byId = dict(jobs.map((j)=>[
                j.ojid,
                j
            ]));
        let byParentId = groupBy(jobs, (j)=>j.ojparent);
        let level0 = sorted(byParentId[null] ?? [], (j)=>j.ojid, true);
        let recurse = (js)=>flatten(js.map((j)=>[
                    j,
                    ...recurse(sorted(byParentId[j.ojid] ?? [], (j)=>j.ojid))
                ]));
        return {
            jobs: recurse(level0),
            byId
        };
    }, [
        jobs
    ]);
    // XXX: filterOptions will unmount if not memoized
    let columns = [
        {
            id: 'id',
            Header: 'ID',
            accessor: (f)=>f.ojid,
            maxWidth: 60
        },
        {
            id: 'name',
            Header: 'Name',
            accessor: (f)=>cleanName(f.name),
            Cell: ({ value, original })=>/*#__PURE__*/ _jsx(Link, {
                    style: {
                        paddingLeft: flat ? 0 : 20 * original.level
                    },
                    classname: classname,
                    tabname: tabname,
                    odid: original.odid,
                    id: `j${original.ojid}`,
                    children: value
                })
        },
        {
            id: 'domain',
            Header: 'Domain',
            accessor: (f)=>/*#__PURE__*/ _jsx(Link, {
                    odid: f.odid,
                    disabled: domains.odid2name[f.odid] === dname,
                    children: domains.odid2name[f.odid]
                }),
            maxWidth: 80
        },
        {
            Header: 'State',
            id: 'state',
            accessor: (f)=>f.state,
            Cell: ({ value, original })=>/*#__PURE__*/ _jsxs("span", {
                    style: {
                        whiteSpace: 'nowrap'
                    },
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                float: 'right'
                            },
                            children: /*#__PURE__*/ _jsx(EventTypeIndicators, {
                                basic: true,
                                ...original.events
                            })
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            title: value,
                            children: /*#__PURE__*/ _jsx(Icon, {
                                ...state_icons[value] || {
                                    name: 'info'
                                }
                            })
                        })
                    ]
                }),
            maxWidth: 130,
            ...filterOptions(keys(state_icons).map((value)=>({
                    value,
                    label: value
                })))
        },
        //~ {
        //~ id: 'type',
        //~ Header: 'Type',
        //~ accessor: f => Type[f.type],
        //~ filterable: false,
        //~ width: 100,
        //~ },
        {
            id: 'user',
            Header: 'User',
            Cell: ({ value, original })=>{
                if (original.type == 10) {
                    return `${value}*`;
                }
                return value;
            },
            accessor: (f)=>f.creatorName,
            maxWidth: 100
        },
        {
            id: 'created',
            Header: compact ? 'Created' : 'Created (Queued)',
            accessor: (f)=>f.created,
            Cell: ({ value, original })=>{
                let s = formatDateTime(value);
                if (!compact && original.started) {
                    // only if > 1sec
                    if ((new Date(original.started) - new Date(value)) / 1000 > 1) {
                        s = `${s} (${formatDistance(value, original.started, {
                            onlyUnits: true
                        })})`;
                    }
                }
                return s;
            }
        },
        {
            id: 'duration',
            Header: 'Duration',
            accessor: (f)=>f.started && f.ended ? differenceInMilliseconds(new Date(f.ended), new Date(f.started)) : null,
            Cell: ({ value, original })=>value ? formatDistance(0, value, {
                    onlyUnits: true
                }) : '',
            maxWidth: 80,
            ...filterNumbers({
                transform: (v)=>v / 1000
            })
        },
        {
            id: 'log',
            Header: 'Log',
            filterable: false,
            accessor: (f)=>f.logSize,
            Cell: ({ value, original })=>value ? /*#__PURE__*/ _jsx("a", {
                    href: `${apiurl}/log/serveJobLog/${original.ojid}`,
                    target: "joblog",
                    children: prettyBytes(value, {
                        precision: compact ? 0 : 3
                    })
                }) : null,
            maxWidth: compact ? 60 : 80
        }
    ];
    if (compact) {
        let allowed = [
            'name',
            'domain',
            'state',
            'created',
            'log'
        ];
        columns = columns.filter((c)=>allowed.includes(c.id));
    }
    let pageSizeOptions = [
        10,
        50,
        100,
        500,
        1000,
        5000
    ];
    if (pageSize && !pageSizeOptions.includes(pageSize)) {
        pageSizeOptions = sorted([
            ...pageSizeOptions,
            pageSize
        ]);
    }
    jobs = index.jobs;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            listOptions && /*#__PURE__*/ _jsx(JobTableMenu, {
                rows: jobs,
                selected: selected,
                options: listOptions
            }),
            /*#__PURE__*/ _jsx(SelectableTable, {
                flex: flex,
                selectable: selectable,
                selectionKey: "ojid",
                onSelectionChange: setSelected,
                className: "-striped -highlight dynHeight",
                showPaginationTop: showPaginationTop,
                showPaginationBottom: false,
                showPagination: true,
                data: jobs,
                columns: columns,
                filterable: filterable,
                defaultFilterMethod: (filter, row)=>String(row[filter.id]).toLowerCase().includes(filter.value),
                defaultPageSize: defaultPageSize,
                minRows: 0,
                noDataText: noDataText,
                loading: loading,
                pageSize: showPaginationTop ? changedPageSize || pageSize : Math.max(5, jobs.length),
                pageSizeOptions: pageSizeOptions,
                onPageSizeChange: setPageSize,
                getTrProps: (state, rowInfo, column)=>({
                        style: {
                            // assume ojid given as j1234 in request.id
                            backgroundColor: rowInfo && rowInfo.row.id == String(request.id).slice(1) ? '#FFD' : undefined
                        }
                    }),
                persist: true,
                persistKey: "JobTableOpts"
            })
        ]
    });
};
const renderCompact = (ojid, compactRenderer, evdata)=>{
    if (!compactRenderer) {
        return `no "compact" renderer for ${evdata.job?.name}`;
    }
    return /*#__PURE__*/ React.createElement(compactRenderer, {
        events: evdata.events,
        eventTypes: evdata.byType,
        ojid
    });
};
export const LastJobInfoLine = ({ name, linkTarget = 'job/id' })=>{
    let [classname, tabname] = linkTarget ? linkTarget.split('/') : [];
    let compactRenderer = JobTemplates[name]?.compact;
    let { ojid } = useLastJob(name);
    let started = useSelector((root)=>root.data.job.info[ojid]?.started);
    let evdata = useJobEvents(compactRenderer ? ojid : null);
    // do not refetch jobinfo, if started is already determined
    let jobinfo = useJobInfo(started ? null : ojid);
    if (!started && !jobinfo.loading) {
        started = jobinfo.started;
    }
    if (!ojid) {
        return null;
    }
    let renderedDate = null;
    if (started) {
        renderedDate = formatDateTime(started);
    }
    let compactLine = null;
    if (compactRenderer && !evdata.loading) {
        compactLine = /*#__PURE__*/ _jsx("span", {
            children: renderCompact(ojid, compactRenderer, evdata)
        });
    }
    let content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            ' am ',
            /*#__PURE__*/ _jsx(Link, {
                classname: classname,
                tabname: tabname,
                id: `j${ojid}`,
                children: renderedDate
            }),
            /*#__PURE__*/ _jsx(XPopup, {
                basic: true,
                flowing: true,
                on: "click",
                trigger: /*#__PURE__*/ _jsx("div", {
                    style: {
                        cursor: 'pointer',
                        float: 'right',
                        marginLeft: '0.5em'
                    },
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        name: "info"
                    })
                }),
                content: /*#__PURE__*/ _jsx(Segment, {
                    children: compactLine
                })
            })
        ]
    });
    //~ }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            ' Letzter ',
            /*#__PURE__*/ _jsx("b", {
                children: cleanName(name)
            }),
            content
        ]
    });
};
export const CompactJobInfo = ({ ojid })=>{
    /* on line event summary
   */ let job = useActiveJobInfo(ojid);
    let compactRenderer = JobTemplates[job.name]?.compact;
    let evdata = useJobEvents(compactRenderer ? ojid : null);
    return renderCompact(ojid, compactRenderer, evdata);
};
export const JobSummary = ({ ojid })=>{
    /* two lines
    - general data
    - comapact summary
  */ let job = useActiveJobInfo(ojid);
    let evdata = useJobEvents(ojid);
    let compactRenderer = JobTemplates[job.name]?.compact;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(JobLabelBase, {
                        ojid: ojid,
                        job: job
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        style: {
                            float: 'right'
                        },
                        children: /*#__PURE__*/ _jsx(EventTypeIndicators, {
                            ...job.events
                        })
                    })
                ]
            }),
            job.name && /*#__PURE__*/ _jsx("div", {
                children: renderCompact(ojid, compactRenderer, evdata)
            })
        ]
    });
};
